import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { useStore } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ShoppingCart, User, LogOut, Search } from "lucide-react";
import { useEffect, useState } from "react";
import NotificationCenter from "@/components/NotificationCenter";

export default function StorefrontLayout() {
  const { slug, tenant, loading, error } = useStore();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const update = () => {
      try {
        const raw = localStorage.getItem(`cart_${slug}`);
        const items = raw ? JSON.parse(raw) : [];
        setCartCount(items.length);
      } catch { setCartCount(0); }
    };
    update();
    window.addEventListener("storage", update);
    window.addEventListener("cart_update", update);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener("cart_update", update);
    };
  }, [slug]);

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading storefront…</div>;
  if (error) return <div className="min-h-screen grid place-items-center text-rose-600">{error}</div>;
  if (!tenant) return null;

  const theme = tenant.branding?.theme || "water_purifier";
  const b = tenant.branding || {};
  const isDark = theme === "automotive";

  return (
    <div className={`min-h-screen store-theme theme-${theme}`}>
      <header
        className={`sticky top-0 z-30 backdrop-blur-md border-b ${isDark ? "bg-black/80 border-neutral-800" : "bg-white/80 border-slate-200"}`}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={`/t/${slug}`} className="flex items-center gap-2 mr-4" data-testid="storefront-logo">
            {b.logo_url && <img src={b.logo_url} alt="" className="h-10 w-10 object-contain rounded" />}
            <div>
              <div className="font-bold text-base leading-none" style={{ fontFamily: b.heading_font, color: b.primary_color }}>{tenant.name}</div>
              <div className="text-[10px] uppercase tracking-wider opacity-60">{tenant.business_type}</div>
            </div>
          </Link>
          <nav className="hidden md:flex gap-4 text-sm ml-auto">
            <Link to={`/t/${slug}`} data-testid="storefront-nav-home">Home</Link>
            <Link to={`/t/${slug}?show=all`} data-testid="storefront-nav-all">Browse All</Link>
            {user?.role === "customer" && <Link to={`/t/${slug}/account`} data-testid="storefront-nav-account">My Account</Link>}
          </nav>
          <div className="ml-auto md:ml-4 flex items-center gap-2">
            <Link to={`/t/${slug}/checkout`} className="relative" data-testid="storefront-cart-link">
              <Button variant="ghost" size="icon" style={{ color: b.primary_color }}>
                <ShoppingCart className="h-5 w-5" />
              </Button>
              {cartCount > 0 && <span className="absolute -top-1 -right-1 text-[10px] rounded-full px-1.5 py-0.5 text-white" style={{ background: b.primary_color }}>{cartCount}</span>}
            </Link>
            {user?.role === "customer" && user?.tenant_id === tenant.id ? (
              <>
                <NotificationCenter accentColor={b.primary_color} />
                <Button variant="ghost" size="sm" data-testid="storefront-account-btn" onClick={() => nav(`/t/${slug}/account`)}><User className="h-4 w-4 mr-1" />{user.name?.split(" ")[0]}</Button>
                <Button variant="ghost" size="icon" onClick={() => { logout(); nav(`/t/${slug}`); }} data-testid="storefront-logout-btn"><LogOut className="h-4 w-4" /></Button>
              </>
            ) : (
              <Button data-testid="storefront-login-btn" onClick={() => nav(`/t/${slug}/login`)} style={{ background: b.primary_color, color: "#fff" }}>Sign in</Button>
            )}
          </div>
        </div>
      </header>

      <main><Outlet /></main>

      <footer className={`border-t ${isDark ? "bg-neutral-950 border-neutral-800 text-neutral-400" : "border-slate-200 t-surface"} mt-12`}>
        <div className="max-w-6xl mx-auto px-4 py-8 text-sm flex flex-col md:flex-row justify-between gap-2">
          <div>© {tenant.name} — Powered by Rental.SaaS</div>
          <div className="flex gap-4">
            <Link to={`/tenant/${slug}/seller/login`} className="opacity-70 hover:opacity-100" data-testid="storefront-seller-link">Seller Login</Link>
            <Link to={`/tenant/${slug}/admin/login`} className="opacity-70 hover:opacity-100" data-testid="storefront-admin-link">Tenant Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
